import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Instagram, Loader2, Eye, EyeOff, CheckCircle2, XCircle, AlertCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { useTranslation } from "@/hooks/useTranslation";
import { getHomePath, getVerifyPath } from "@/utils/localePaths";

// Wave shimmer effect - each letter scales independently with color shimmer
const shimmerStyles = `
  @keyframes wave {
    0%, 100% {
      transform: scale(1);
      filter: brightness(1);
    }
    25% {
      transform: scale(1.25);
      filter: brightness(1.4);
    }
    50%, 100% {
      transform: scale(1);
      filter: brightness(1);
    }
  }
  
  .shimmer-text {
    display: inline-flex;
    gap: 0;
    color: #FFDD59;
  }
  
  .shimmer-text span {
    display: inline-block;
    animation: wave 4s ease-in-out infinite;
  }
  
  /* Stagger the wave effect for each letter - narrower wave */
  .shimmer-text span:nth-child(1) { animation-delay: 0s; }
  .shimmer-text span:nth-child(2) { animation-delay: 0.05s; }
  .shimmer-text span:nth-child(3) { animation-delay: 0.1s; }
  .shimmer-text span:nth-child(4) { animation-delay: 0.15s; }
  .shimmer-text span:nth-child(5) { animation-delay: 0.2s; }
  .shimmer-text span:nth-child(6) { animation-delay: 0.25s; }
  .shimmer-text span:nth-child(7) { animation-delay: 0.3s; }
  .shimmer-text span:nth-child(8) { animation-delay: 0.35s; }
  .shimmer-text span:nth-child(9) { animation-delay: 0.4s; }
  .shimmer-text span:nth-child(10) { animation-delay: 0.45s; }
  .shimmer-text span:nth-child(11) { animation-delay: 0.5s; }
  .shimmer-text span:nth-child(12) { animation-delay: 0.55s; }
  .shimmer-text span:nth-child(13) { animation-delay: 0.6s; }
  .shimmer-text span:nth-child(14) { animation-delay: 0.65s; }
  .shimmer-text span:nth-child(15) { animation-delay: 0.7s; }
  .shimmer-text span:nth-child(16) { animation-delay: 0.75s; }
  .shimmer-text span:nth-child(17) { animation-delay: 0.8s; }
  .shimmer-text span:nth-child(18) { animation-delay: 0.85s; }
  .shimmer-text span:nth-child(19) { animation-delay: 0.9s; }
  .shimmer-text span:nth-child(20) { animation-delay: 0.95s; }
  .shimmer-text span:nth-child(21) { animation-delay: 1s; }
  .shimmer-text span:nth-child(22) { animation-delay: 1.05s; }
  .shimmer-text span:nth-child(23) { animation-delay: 1.1s; }
  .shimmer-text span:nth-child(24) { animation-delay: 1.15s; }
  .shimmer-text span:nth-child(25) { animation-delay: 1.2s; }
  .shimmer-text span:nth-child(26) { animation-delay: 1.25s; }
  .shimmer-text span:nth-child(27) { animation-delay: 1.3s; }
  .shimmer-text span:nth-child(28) { animation-delay: 1.35s; }
  .shimmer-text span:nth-child(29) { animation-delay: 1.4s; }
  .shimmer-text span:nth-child(30) { animation-delay: 1.45s; }
  .shimmer-text span:nth-child(31) { animation-delay: 1.5s; }
  .shimmer-text span:nth-child(32) { animation-delay: 1.55s; }
`;

function RegisterPage() {
  const { t, locale, isRTL } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Password validation states
  const [passwordChecks, setPasswordChecks] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState(null); // "login", "register" or null (no form shown)
  const [kvkkAccepted, setKvkkAccepted] = useState(false); // KVKK checkbox
  const [registrationDisabled, setRegistrationDisabled] = useState(false); // Registration status - ENABLED for testing

  // 2FA State
  const [show2FA, setShow2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [isFirstSetup, setIsFirstSetup] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();
  
  // Form refs for validation messages
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const firstNameRef = useRef(null);
  
  // Update password checks as user types
  useEffect(() => {
    setPasswordChecks({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?\/~`]/.test(password)
    });
  }, [password]);
  
  // Load last used email from localStorage on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('lastUsedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
    }
    
    // Check for OAuth error in URL
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    if (error) {
      // OAuth error messages - will be translated via locale files
      toast.error(t('common.error'));
      // Clear error from URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Check if registration is enabled
    const checkRegistrationStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/`);
        const data = await response.json();
        // If we get here, API is working
      } catch (error) {
        console.error("Backend connection error:", error);
      }
    };
    
    checkRegistrationStatus();
  }, []);
  const lastNameRef = useRef(null);

  // Set custom validation messages based on locale
  useEffect(() => {
    const setValidationMessages = () => {
      if (emailRef.current) {
        emailRef.current.oninvalid = (e) => {
          e.target.setCustomValidity("");
          if (!e.target.validity.valid) {
            if (e.target.validity.valueMissing) {
              e.target.setCustomValidity(t('auth.validation.fill_field'));
            } else if (e.target.validity.typeMismatch) {
              e.target.setCustomValidity(t('auth.validation.valid_email'));
            }
          }
        };
        emailRef.current.oninput = (e) => e.target.setCustomValidity("");
      }
      
      if (passwordRef.current) {
        passwordRef.current.oninvalid = (e) => {
          e.target.setCustomValidity("");
          if (!e.target.validity.valid) {
            if (e.target.validity.valueMissing) {
              e.target.setCustomValidity(t('auth.validation.fill_field'));
            } else if (e.target.validity.tooShort) {
              e.target.setCustomValidity(t('auth.validation.password_length'));
            }
          }
        };
        passwordRef.current.oninput = (e) => e.target.setCustomValidity("");
      }
      
      if (firstNameRef.current) {
        firstNameRef.current.oninvalid = (e) => {
          e.target.setCustomValidity("");
          if (!e.target.validity.valid) {
            if (e.target.validity.valueMissing) {
              e.target.setCustomValidity(t('auth.validation.fill_field'));
            } else if (e.target.validity.tooShort) {
              e.target.setCustomValidity(t('auth.validation.name_length'));
            }
          }
        };
        firstNameRef.current.oninput = (e) => e.target.setCustomValidity("");
      }
      
      if (lastNameRef.current) {
        lastNameRef.current.oninvalid = (e) => {
          e.target.setCustomValidity("");
          if (!e.target.validity.valid) {
            if (e.target.validity.valueMissing) {
              e.target.setCustomValidity(t('auth.validation.fill_field'));
            } else if (e.target.validity.tooShort) {
              e.target.setCustomValidity(t('auth.validation.surname_length'));
            }
          }
        };
        lastNameRef.current.oninput = (e) => e.target.setCustomValidity("");
      }
    };
    
    setValidationMessages();
  }, [selectedMode, t]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Terms checkbox check
    if (!kvkkAccepted) {
      toast.error(t('auth.register.terms_error'));
      return;
    }
    
    setLoading(true);

    try {
      // SPEC: profil-tipi sorusu (ve ona bagli ek-bilgi girisi) kayittan kaldirildi (Google paritesi)
      const result = await register(email, password, firstName, lastName, "", false, locale);
      
      if (result.success) {
        // Save email to localStorage for future use
        localStorage.setItem('lastUsedEmail', email);
        
        // Check if email verification is required
        if (result.requiresVerification) {
          toast.info(t('auth.register.email_verification'));
          navigate(getVerifyPath(locale));
        } else {
          navigate(getHomePath(locale));
        }
      } else {
        // Check if registration is disabled - C3a-3: kod-tabanli (locale-bagimsiz, eski TR string-match yerine)
        if (result.code === 'auth.register.disabled') {
          setRegistrationDisabled(true);
        }
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const result = await login(email, password, show2FA ? totpCode : null);
      
      // Check if 2FA is required
      if (result.requires2FA) {
        setShow2FA(true);
        setTempToken(result.tempToken);
        setQrCode(result.qrCode);
        setIsFirstSetup(result.isFirstSetup);
        return;
      }
      
      if (result.success) {
        // Save email to localStorage for future use
        localStorage.setItem('lastUsedEmail', email);
        navigate(`/${locale}/`);
      } else {
        toast.error(result.error || t('auth.login.error_credentials'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      toast.error(t('auth.login.error_2fa_code_required'));
      return;
    }
    setLoading(true);
    try {
      const result = await login(email, password, totpCode);
      if (result.success) {
        toast.success(t('auth.login.success'));
        setShow2FA(false);
        navigate(`/${locale}/`);
      } else {
        toast.error(result.error || t('auth.login.error_2fa_wrong'));
      }
    } catch (error) {
      toast.error(t('auth.login.error_2fa_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex flex-col">
      {/* Header - Mobil uyumlu */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-2 sm:px-4">
        <div className="mb-1 px-2 sm:px-4">
          <div className="flex justify-between items-center gap-2 sm:gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
            {/* Sol: Logo - Tıklanabilir, Ana Sayfaya Dön */}
            <div 
                  className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                  onClick={() => navigate('/')}
                >
                  <img 
                    src="/opara-logo-trimmed.png" 
                    alt="OPARA Logo" 
                    className="h-8 sm:h-10 w-auto object-contain"
                  />
                </div>
            
            {/* Sağ: Boş (giriş sayfası için buton yok) */}
            <div className="flex items-center gap-2 sm:gap-3">
            </div>
          </div>
        </div>
      </header>
      
      {/* Geri Dön Butonu - Header altında, header ile aynı hizada */}
      <div className="px-2 sm:px-4 mb-2">
        <div className="px-2 sm:px-4">
          <Button 
            onClick={() => navigate(-1)}
            className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-sm sm:text-base"
          >
            {t('common.back')}
          </Button>
        </div>
      </div>
      
      {/* Container ortada */}
      <div className="flex-1 flex flex-col items-center justify-center p-2 sm:p-4">
        <div className="w-full max-w-2xl flex flex-col items-center gap-4 sm:gap-6">

        {/* Kayıt/Giriş Formu - Kare Card */}
        <Card className="w-full max-w-md bg-gray-900 border-2 border-[#FFDD59]">
          <CardHeader className="text-center">
            {/* Üç Seçenek Butonu */}
            <div className="space-y-3 mb-6">
              {/* 1. Google ile Devam Et - WHITE BACKGROUND */}
              <Button
                type="button"
                onClick={async () => {
                  // Direkt Google OAuth'a yönlendir
                  try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    console.log('[Google OAuth] Backend URL:', backendUrl);
                    console.log('[Google OAuth] Fetching from:', `${backendUrl}/api/native-oauth/google/login/`);
                    
                    const response = await fetch(`${backendUrl}/api/native-oauth/google/login/?locale=${locale}`, {
                      method: 'GET',
                      credentials: 'include',
                      headers: {
                        'Accept': 'application/json',
                      }
                    });
                    
                    console.log('[Google OAuth] Response status:', response.status);
                    console.log('[Google OAuth] Response headers:', Object.fromEntries(response.headers.entries()));
                    
                    if (!response.ok) {
                      const errorText = await response.text();
                      console.error('[Google OAuth] Backend error:', errorText);
                      throw new Error(`Backend returned ${response.status}: ${errorText}`);
                    }
                    
                    const data = await response.json();
                    console.log('[Google OAuth] Received data:', data);
                    
                    if (data.auth_url) {
                      console.log('[Google OAuth] Redirecting to Google:', data.auth_url);
                      window.location.href = data.auth_url;
                    } else {
                      throw new Error('Auth URL not received from backend');
                    }
                  } catch (error) {
                    console.error('[Google OAuth] ERROR:', error);
                    console.error('[Google OAuth] Error type:', error.name);
                    console.error('[Google OAuth] Error message:', error.message);
                    
                    // More specific error message
                    if (error.name === 'TypeError' && error.message.includes('fetch')) {
                      alert(t('auth.oauth.connection_error'));
                    } else {
                      alert(t('auth.oauth.google_failed', { error: error.message }));
                    }
                  }
                }}
                className="w-full h-16 transition-all flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-black font-bold text-base border-2 border-gray-300"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.register.google_btn')}
              </Button>

              {/* 2. Manuel Giriş - GREY BACKGROUND, YELLOW HOVER */}
              <Button
                type="button"
                onClick={() => setSelectedMode("login")}
                className="w-full h-16 transition-all flex flex-col items-center justify-center bg-gray-700 text-white hover:bg-[#FFDD59] hover:text-black"
              >
                <span className="text-sm font-normal">{t('auth.register.has_account')}</span>
                <span className="text-base font-bold mt-0.5">{t('auth.login.title')}</span>
              </Button>

              {/* 3. Manuel Kayıt - GREY BACKGROUND, YELLOW HOVER */}
              <Button
                type="button"
                onClick={() => setSelectedMode("register")}
                className="w-full h-16 transition-all flex flex-col items-center justify-center bg-gray-700 text-white hover:bg-[#FFDD59] hover:text-black"
              >
                <span className="text-sm font-normal">{t('auth.register.no_account')}</span>
                <span className="text-base font-bold mt-0.5">{t('auth.register.signup_btn')}</span>
              </Button>
            </div>
          </CardHeader>
        
        <CardContent id="register-form">
          {/* LOGIN MODE */}
          {selectedMode === "login" && (
            show2FA ? (
              <div>
                <div className="text-center mb-6">
                  <ShieldCheck className="w-16 h-16 text-[#FFDD59] mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t('auth.twofa.title')}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    {isFirstSetup
                      ? t('auth.twofa.desc_setup')
                      : t('auth.twofa.desc_normal')}
                  </p>
                </div>
                
                {/* QR Code for first setup */}
                {isFirstSetup && qrCode && (
                  <div className="mb-6 p-4 bg-white rounded-lg flex justify-center">
                    <img src={`data:image/png;base64,${qrCode}`} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                )}
                
                <form onSubmit={handle2FASubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('auth.twofa.code_label')}
                    </label>
                    <Input
                      type="text"
                      data-testid="2fa-totp-input"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="bg-gray-800 border-gray-700 text-white text-center text-2xl tracking-widest"
                      autoFocus
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="2fa-verify-button"
                    className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-semibold py-3"
                    disabled={loading || totpCode.length !== 6}
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      t('auth.twofa.verify_btn')
                    )}
                  </Button>
                  
                  <button
                    type="button"
                    data-testid="2fa-back-button"
                    onClick={() => {
                      setShow2FA(false);
                      setTotpCode("");
                    }}
                    className="w-full text-gray-400 hover:text-white text-sm mt-2"
                  >
                    {t('auth.twofa.back_btn')}
                  </button>
                </form>
              </div>
            ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              {/* E-posta Adresi */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.login.email_label')}
                </label>
                <Input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              {/* Şifre */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.login.password_label')}
                </label>
                <div className="relative">
                  <Input
                    ref={passwordRef}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••"
                    required
                    minLength={6}
                    className="bg-gray-800 border-gray-700 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Forgot Password Link */}
                <div className="text-right mt-2">
                  <Link 
                    to={locale === 'tr' ? "/tr/sifremi-unuttum" : "/forgot-password"} 
                    className="text-sm text-[#FFDD59] hover:text-[#E6C84D] transition-colors"
                  >
                    {t('auth.login.forgot_password')}
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-base py-6"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('auth.login.loading')}
                  </>
                ) : (
                  t('auth.login.submit_btn')
                )}
              </Button>
            </form>
            )
          )}

          {/* REGISTER MODE */}
          {selectedMode === "register" && (
            <form onSubmit={handleRegister} className="space-y-4">
              {/* REGISTER MODE - All Fields */}
                
                {/* Registration Disabled Warning */}
                {registrationDisabled && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🚧</span>
                      <div>
                        <h3 className="text-lg font-semibold text-yellow-400 mb-1">
                          {t('auth.register.coming_soon_title')}
                        </h3>
                        <p className="text-sm text-yellow-300">
                          {t('auth.register.coming_soon_desc')}
                          <br />
                          <span className="text-xs text-yellow-400/80 mt-1 inline-block">
                            {t('auth.register.coming_soon_note')}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Info Message */}
                {!registrationDisabled && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                    <p className="text-sm text-green-400 text-center">
                      {t('auth.register.free_credit_info')}
                    </p>
                  </div>
                )}

            {/* First Name and Last Name - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.register.first_name')}
                </label>
                <Input
                  ref={firstNameRef}
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('auth.register.first_name_placeholder')}
                  required
                  minLength={2}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.register.last_name')}
                </label>
                <Input
                  ref={lastNameRef}
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('auth.register.last_name_placeholder')}
                  required
                  minLength={2}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            {/* Email and Password - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.register.email_label')}
                </label>
                <Input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.register.email_placeholder')}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.register.password_label')}
                </label>
                <div className="relative">
                  <Input
                    ref={passwordRef}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••"
                    required
                    minLength={8}
                    className="bg-gray-800 border-gray-700 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Requirements - Show when typing */}
                {password && (
                  <div className="mt-2 bg-gray-800/50 rounded-lg p-3 space-y-1">
                    <p className="text-xs font-medium text-gray-300 mb-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t('auth.password.requirements')}
                    </p>
                    <PasswordCheck met={passwordChecks.minLength} text={t('auth.password.min_length')} />
                    <PasswordCheck met={passwordChecks.hasUppercase} text={t('auth.password.uppercase')} />
                    <PasswordCheck met={passwordChecks.hasLowercase} text={t('auth.password.lowercase')} />
                    <PasswordCheck met={passwordChecks.hasNumber} text={t('auth.password.number')} />
                    <PasswordCheck met={passwordChecks.hasSpecial} text={t('auth.password.special')} />
                  </div>
                )}
              </div>
            </div>

            {/* Terms & Privacy Checkbox - Required */}
            <div className="flex items-start gap-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <input
                type="checkbox"
                id="kvkk-checkbox"
                checked={kvkkAccepted}
                onChange={(e) => setKvkkAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-600 text-[#FFDD59] focus:ring-[#FFDD59] focus:ring-offset-gray-900 cursor-pointer"
              />
              <label htmlFor="kvkk-checkbox" className="text-sm text-gray-300 cursor-pointer leading-relaxed">
                {t('auth.register.terms_consent').split(/(\{TERMS\}|\{PRIVACY\})/).map((seg, i) => {
                  if (seg === '{TERMS}') return <Link key={i} to={locale === 'tr' ? "/tr/kullanim-kosullari" : "/terms"} className="text-[#FFDD59] hover:underline">{t('auth.register.terms_link')}</Link>;
                  if (seg === '{PRIVACY}') return <Link key={i} to={locale === 'tr' ? "/tr/gizlilik" : "/privacy"} className="text-[#FFDD59] hover:underline">{t('auth.register.privacy_link')}</Link>;
                  return seg;
                })}
              </label>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !kvkkAccepted || registrationDisabled}
              className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-base py-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('auth.register.loading')}
                </>
              ) : registrationDisabled ? (
                t('auth.register.disabled')
              ) : (
                t('auth.register.submit_btn')
              )}
            </Button>
            </form>
          )}
        </CardContent>
      </Card>
        </div>
      </div>
      
      {/* Footer - Same across all pages */}
      <Footer />
      
      {/* Language Sidebar - Vertical on right side */}
      {/* Language Sidebar removed */}
    </div>
  );
}

// Password Check Component
function PasswordCheck({ met, text }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {met ? (
        <CheckCircle2 className="w-3 h-3 text-green-500" />
      ) : (
        <XCircle className="w-3 h-3 text-gray-500" />
      )}
      <span className={met ? "text-green-500" : "text-gray-400"}>
        {text}
      </span>
    </div>
  );
}

export default RegisterPage;
